import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuthStore } from '../../stores/authStore';

type RedeemResponse = {
  success: boolean;
  user: {
    id: string;
    email: string;
    role: string;
  };
  nextPath: string;
  targetPath?: string;
};

export default function ActionSsoPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { fetchUser } = useAuthStore();
  const [status, setStatus] = useState<'loading' | 'error'>('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    const redeem = async () => {
      const token = searchParams.get('token') || '';
      if (!token) {
        if (!active) return;
        setStatus('error');
        setError('Missing action token in URL.');
        return;
      }

      try {
        const response = await api.post<RedeemResponse>('/action-links/redeem', { token });
        await fetchUser();
        if (!active) return;
        const nextPath = response.data?.nextPath || response.data?.targetPath || '/kol/dashboard';
        navigate(nextPath, { replace: true });
      } catch (err: any) {
        if (!active) return;
        const serverMsg = err?.response?.data?.error;
        setStatus('error');
        setError(serverMsg || 'Unable to redeem this action link.');
      }
    };
    redeem();
    return () => {
      active = false;
    };
  }, [searchParams, fetchUser, navigate]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6 shadow-sm">
        {status === 'loading' ? (
          <>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Secure sign-in link</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Verifying your one-time link and signing you in...
            </p>
          </>
        ) : (
          <>
            <h1 className="text-xl font-semibold text-red-700 dark:text-red-400">Action link failed</h1>
            <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">{error}</p>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="mt-4 inline-flex items-center rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
            >
              Go to login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
